import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Editor } from "@tinymce/tinymce-react";
import E3WijmoGrid from "../DataGrid/E3WijmoGrid";
import PropTypes from "prop-types";

const BoardEditor = forwardRef(({ onInit, ...props }, ref) => {
  const editorRef = useRef(null);
  const [isReady, setReady] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useImperativeHandle(ref, () => ({
    isReady: isReady,
    /**
     * 에디터의 현재 콘텐츠를 반환합니다.
     * @returns {string} 에디터에 작성된 HTML 콘텐츠
     */
    getValue: () => {
      return editorRef.current.getContent();
    },

    /**
     * 에디터의 콘텐츠를 설정합니다.
     * @param {string} body - 설정할 HTML 콘텐츠
     * @returns {boolean} 콘텐츠를 성공적으로 설정하면 `true`, 아니면 `false`
     */
    setValue: (body) => {
      return isReady ? editorRef.current.setContent(body) : false;
    },

    /**
     * 에디터를 비활성화합니다.
     */

    disable: () => {
      setIsDisabled(true); // 외부에서 disabled 상태 변경 가능
    },

    /**
     * 에디터를 활성화합니다.
     */

    enable: () => {
      setIsDisabled(false); // 외부에서 disabled 상태 변경 가능
    },
  }));

  return (
    <>
      <Editor
        tinymceScriptSrc={
          import.meta.env.VITE_PUBLIC_URL + "/tinymce/tinymce.min.js"
        }
        onInit={(evt, editor) => {
          editorRef.current = editor;
          setReady(true);
          if (onInit !== undefined) {
            onInit(true);
          }
        }}
        disabled={isDisabled}
        initialValue=""
        init={{
          max_height: props.maxHeight,
          height: props.height,
          resize: props.resize,
          menubar: props.menubar,
          language: "ko_KR",
          language_url:
            import.meta.env.VITE_PUBLIC_URL + "/tinymce/langs/ko_KR.js",
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "preview",
            "help",
            "wordcount",
          ],
          toolbar:
            "undo redo | blocks | fontsize " +
            "bold italic forecolor | alignleft aligncenter" +
            "alignright alignjustify | bullist numlist outdent indent | " +
            "removeformat | help",
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; overflow: hidden; max-height: 400px; }",
          placeholder: props.placeHolder,
          extended_valid_elements:
            "iframe[src|width|height|frameborder|allowfullscreen]",
          valid_children: "+body[iframe]",
          setup: function (editor) {
            editor.on("keydown", function (event) {
              const body = editor.getBody();
              if (
                body.scrollHeight > stateHeights &&
                event.key !== "Backspace" &&
                event.key !== "Delete"
              ) {
                event.preventDefault(); // 입력 차단
              }
            });
          },
        }}
      />
    </>
  );
});

BoardEditor.propTypes = {
  height: PropTypes.number,
  maxHeight: PropTypes.number,
  resize: PropTypes.bool,
  menubar: PropTypes.bool,
  placeHolder: PropTypes.string,
  onInit: PropTypes.func,
};

BoardEditor.defaultProps = {
  height: 300,
  maxHeight: 1080,
  resize: false,
  menubar: false,
  placeHolder: "내용을 입력하세요",
  onInit: undefined,
};

/**
 * BoardEditor에서 노출되는 ref 함수 PropTypes 정의
 * @typedef {Object} BoardEditorRef
 * @property {function(): string} getValue - 에디터의 콘텐츠를 반환
 * @property {function(string): boolean} setValue - 에디터의 콘텐츠를 설정
 * @property {function(boolean): void} disable - 에디터 비활성화
 */

BoardEditor.displayName = "BoardEditor";
export default BoardEditor;
